import React, { useEffect, useRef, useState } from 'react';
import { tokenStore } from '../lib/api';
import { getJwtExpiryMs } from '../lib/jwt';
import SessionTimeoutDialog from './SessionTimeoutDialog';

/**
 * SessionTimeoutManager
 *
 * Watches the persisted Supabase access token and surfaces a friendly
 * "Session expiring soon" dialog 60 seconds before the JWT's `exp` claim.
 *
 *   • Triggered purely by hard token expiration — Supabase access tokens
 *     have a fixed lifetime (default 1h). There is no idle timeout.
 *   • Re-evaluates the schedule whenever a new token is persisted (login,
 *     invite redemption, etc.) — see the storage event + a re-check whenever
 *     `userId` changes.
 *   • "Continue for now" simply dismisses the dialog. It does NOT extend
 *     the token (no refresh-token path on the frontend) — the countdown
 *     keeps running in the background. When the timer hits 0, or when any
 *     API call returns 401, the user is redirected cleanly to
 *     /login?reason=timeout.
 *   • "Log out now" runs onLogout (provided by AuthContext).
 *
 * This component renders no UI of its own; the dialog handles all rendering.
 */
const WARNING_LEAD_MS = 60 * 1000; // 60 seconds before expiry

export default function SessionTimeoutManager({ userId, onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const warnTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  // Clear all timers when unmounting / re-scheduling.
  const clearTimers = () => {
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  };

  // Whenever the user changes (login/logout) re-evaluate the schedule.
  useEffect(() => {
    clearTimers();
    setShowWarning(false);

    if (!userId) return undefined;

    const token = tokenStore.get();
    const expMs = getJwtExpiryMs(token);
    if (!expMs) return undefined;

    const now = Date.now();
    const msUntilWarn = expMs - WARNING_LEAD_MS - now;

    if (msUntilWarn <= 0) {
      // Already inside the warning window (or past expiry). Show right away.
      const secsLeft = Math.max(0, Math.floor((expMs - now) / 1000));
      setSecondsLeft(secsLeft);
      setShowWarning(true);
      startTick(expMs);
    } else {
      warnTimerRef.current = setTimeout(() => {
        const secs = Math.max(0, Math.floor((expMs - Date.now()) / 1000));
        setSecondsLeft(secs);
        setShowWarning(true);
        startTick(expMs);
      }, msUntilWarn);
    }

    return clearTimers;
  }, [userId]);

  const startTick = (expMs) => {
    tickTimerRef.current = setInterval(() => {
      const secs = Math.max(0, Math.floor((expMs - Date.now()) / 1000));
      setSecondsLeft(secs);
      if (secs <= 0) {
        clearTimers();
        // Token has now actually expired. Force a clean logout.
        onLogout?.('expired');
      }
    }, 1000);
  };

  const handleContinueForNow = () => {
    // The button just dismisses the modal. We deliberately do NOT call
    // /auth/me here — that would imply the session was extended, which it
    // is not (no refresh-token path on the frontend). The countdown keeps
    // running in the background; when it hits 0 (or any API call returns
    // 401), the 401 interceptor / timer-expiry path redirects cleanly.
    setShowWarning(false);
  };

  const handleLogoutNow = () => {
    setShowWarning(false);
    clearTimers();
    onLogout?.('user');
  };

  if (!showWarning) return null;
  return (
    <SessionTimeoutDialog
      secondsLeft={secondsLeft}
      onStay={handleContinueForNow}
      onLogout={handleLogoutNow}
    />
  );
}
